import axios from "axios";
import { auth, getCurrentIdToken } from "firebaseClient";
import { getVisitorId } from "visitorId";
import { getDeviceType } from "deviceType";

const API_URL = process.env.REACT_APP_URL;

// Attach a fresh Firebase ID token to every request from a signed-in user.
// getIdToken() returns the cached token and transparently refreshes it when it's
// near/after expiry, so this avoids both the "token not ready yet" race right
// after login and stale-token 403s during long dashboard sessions.
axios.interceptors.request.use(async (config) => {
  const user = auth.currentUser;
  if (user) {
    const token = await user.getIdToken();
    config.headers = config.headers || {};
    config.headers.Authorization = `Bearer ${token}`;
  }
  return config;
});

function getHeaders() {
  return {
    Authorization: `Bearer ${getCurrentIdToken()}`,
    "Content-Type": "application/json",
  };
}

export async function getReservationById(id, signal) {
  try {
    const response = await axios({
      method: "get",
      url: `${API_URL}api/reservations/id/${id}`,
      signal,
    });
    return response.data;
  } catch (error) {
    if (axios.isCancel(error)) {
      console.log("Request canceled", error.message);
    } else {
      throw error;
    }
  }
}

export async function getReservationsByDate(date, signal) {
  try {
    const response = await axios({
      method: "get",
      url: `${API_URL}api/reservations/date/${date}`,
      headers: getHeaders(),
      signal,
    });
    return response.data;
  } catch (error) {
    if (axios.isCancel(error)) {
      console.log("Request canceled", error.message);
    } else {
      throw error;
    }
  }
}

export async function getTimeListByDate(date) {
  try {
    const response = await axios({
      method: "get",
      url: `${API_URL}api/reservations/timelist?date=${date}`,
    });
    return response.data;
  } catch (error) {
    if (axios.isCancel(error)) {
      console.log("Request canceled", error.message);
    } else {
      throw error;
    }
  }
}

export async function patchReservation(id, state, user) {
  try {
    const response = await axios({
      method: "patch",
      url: `${API_URL}api/reservations/id/${id}/state/${state}/user/${user}`,
    });
    return response;
  } catch (error) {
    throw error;
  }
}

export async function updateReservation(id, updatedRes) {
  try {
    const response = await axios({
      method: "put",
      url: `${API_URL}api/reservations/id/${id}/`,
      data: updatedRes,
    });
    return response;
  } catch (error) {
    throw error;
  }
}

export async function deleteTimeblock(id) {
  try {
    const response = await axios({
      method: "delete",
      url: `${API_URL}api/reservations/timeblock/${id}`,
      headers: getHeaders(),
    });
    return response;
  } catch (error) {
    throw error;
  }
}

export async function updateTimeblock(id, updatedBlock) {
  try {
    const response = await axios({
      method: "put",
      url: `${API_URL}api/reservations/timeblock/${id}/`,
      headers: getHeaders(),
      data: updatedBlock,
    });
    return response;
  } catch (error) {
    throw error;
  }
}

export async function checkReservation(
  numGuests,
  date,
  time,
  signal,
  override = false
) {
  try {
    const response = await axios({
      method: "get",
      url: `${API_URL}api/reservations/check`,
      params: {
        numGuests: numGuests,
        date: date,
        time: time,
        override: override,
      },
      signal,
    });
    return response.data;
  } catch (error) {
    if (axios.isCancel(error)) {
      console.log("Request canceled", error.message);
    } else {
      throw error;
    }
  }
}

export async function getTopCustomers() {
  try {
    const response = await axios({
      method: "get",
      url: `${API_URL}api/reservations/top-customers`,
      headers: getHeaders(),
    });
    return response.data;
  } catch (error) {
    throw error;
  }
}

export async function getCustomerDetail(phone) {
  try {
    const response = await axios({
      method: "get",
      url: `${API_URL}api/reservations/customer-detail/${phone}`,
      headers: getHeaders(),
    });
    return response.data;
  } catch (error) {
    throw error;
  }
}

export async function fetchCustomerHistory(phone, signal) {
  try {
    const response = await axios({
      method: "get",
      url: `${API_URL}api/reservations/history/${phone}`,
      headers: getHeaders(),
      signal,
    });
    return response.data;
  } catch (error) {
    if (axios.isCancel(error)) {
      console.log("Request canceled", error.message);
    } else {
      throw error;
    }
  }
}

export async function postTimeBlock(newBlock) {
  try {
    const response = await axios({
      method: "post",
      url: `${API_URL}api/reservations/timeblock`,
      headers: getHeaders(),
      data: newBlock,
    });
    return response;
  } catch (error) {
    throw error;
  }
}

export async function postReservation(newRes) {
  try {
    const response = await axios({
      method: "post",
      url: `${API_URL}api/reservations/`,
      data: newRes,
    });
    return response;
  } catch (error) {
    throw error;
  }
}

export async function postAdminReservation(newRes) {
  try {
    const response = await axios({
      method: "post",
      url: `${API_URL}api/reservations/override`,
      headers: getHeaders(),
      data: newRes,
    });
    return response;
  } catch (error) {
    throw error;
  }
}

export async function postContact(newContact) {
  try {
    const response = await axios({
      method: "post",
      url: `${API_URL}api/messages`,
      data: newContact,
    });
    return response.status;
  } catch (error) {
    throw error;
  }
}

export async function postGiftcard(newGiftcard) {
  try {
    const response = await axios({
      method: "post",
      url: `${API_URL}api/giftcard`,
      data: newGiftcard,
    });
    return response;
  } catch (error) {
    throw error;
  }
}

export async function getMenus(signal) {
  try {
    const response = await axios({
      method: "get",
      url: `${API_URL}api/order/menus`,
      signal,
    });
    return response;
  } catch (error) {
    if (axios.isCancel(error)) {
      console.log(error.message);
    } else {
      throw error;
    }
  }
}

export async function checkForUpdate(signal) {
  try {
    const response = await axios({
      method: "get",
      url: `${API_URL}api/order/menu-check`,
      signal,
    });
    return response.data;
  } catch (error) {
    if (axios.isCancel(error)) {
      console.log("Request canceled", error.message);
    } else {
      throw error;
    }
  }
}

export async function checkoutCart(newOrder) {
  try {
    const response = await axios({
      method: "post",
      url: `${API_URL}api/order/checkout`,
      data: newOrder,
    });
    return response;
  } catch (error) {
    if (axios.isCancel(error)) {
      console.log("Request canceled", error.message);
    } else {
      throw error;
    }
  }
}

export async function placePickupOrder(newOrder) {
  try {
    const response = await axios({
      method: "post",
      url: `${API_URL}api/order/pickup`,
      data: newOrder,
    });
    return response;
  } catch (error) {
    if (axios.isCancel(error)) {
      console.log("Request canceled", error.message);
    } else {
      throw error;
    }
  }
}

export async function getOrders(signal) {
  try {
    const response = await axios({
      method: "get",
      url: `${API_URL}api/order/today`,
      signal,
    });
    return response.data;
  } catch (error) {
    if (axios.isCancel(error)) {
      console.log("Request canceled", error.message);
    } else {
      throw error;
    }
  }
}

export async function getOrderById(id, signal) {
  try {
    const response = await axios({
      method: "get",
      url: `${API_URL}api/order/id/${id}`,
      signal,
    });
    return response.data;
  } catch (error) {
    if (axios.isCancel(error)) {
      console.log("Request canceled", error.message);
    } else {
      throw error;
    }
  }
}

export async function patchOrderConfirm(id, time) {
  try {
    const response = await axios({
      method: "patch",
      url: `${API_URL}api/order/id/${id}`,
      data: {
        time,
      },
    });
    return response.data;
  } catch (error) {
    if (axios.isCancel(error)) {
      console.log("Request canceled", error.message);
    } else {
      throw error;
    }
  }
}

export async function markComplete(id) {
  try {
    const response = await axios({
      method: "put",
      url: `${API_URL}api/order/id/${id}`,
    });
    return response.data;
  } catch (error) {
    if (axios.isCancel(error)) {
      console.log("Request canceled", error.message);
    } else {
      throw error;
    }
  }
}

export async function getSystemStatus(signal) {
  try {
    const response = await axios({
      method: "get",
      url: `${API_URL}api/order/status`,
      signal,
    });
    return response.data;
  } catch (error) {
    if (axios.isCancel(error)) {
      console.log("Request canceled", error.message);
    } else {
      throw error;
    }
  }
}

export async function patchSystemStatus(status) {
  try {
    const response = await axios({
      method: "patch",
      url: `${API_URL}api/order/status`,
      data: status,
    });
    return response.data;
  } catch (error) {
    if (axios.isCancel(error)) {
      console.log("Request canceled", error.message);
    } else {
      throw error;
    }
  }
}

export async function deleteOrder(id) {
  try {
    await axios({
      method: "delete",
      url: `${API_URL}api/order/id/${id}`,
      data: id,
    });
  } catch (error) {
    if (axios.isCancel(error)) {
      console.log("Request canceled", error.message);
    } else {
      throw error;
    }
  }
}

export async function getEmployees() {
  try {
    const response = await axios({
      method: "get",
      url: `${API_URL}api/payroll/employees`,
      headers: getHeaders(),
    });
    return response.data;
  } catch (error) {
    throw error;
  }
}

export async function updateEmployee(employee) {
  try {
    const response = await axios({
      method: "put",
      url: `${API_URL}api/payroll/employee`,
      headers: getHeaders(),
      data: employee,
    });
    return response.data;
  } catch (error) {
    throw error;
  }
}

export async function getPayrollByPeriod(period) {
  try {
    const response = await axios({
      method: "get",
      url: `${API_URL}api/payroll/`,
      headers: getHeaders(),
      params: { period },
    });
    return response.data;
  } catch (error) {
    throw error;
  }
}

export async function savePayroll(payrollData) {
  try {
    const response = await axios({
      method: "post",
      url: `${API_URL}api/payroll/`,
      headers: getHeaders(),
      data: payrollData,
    });
    return response.data;
  } catch (error) {
    throw error;
  }
}

export async function deletePayroll(period) {
  try {
    const response = await axios({
      method: "delete",
      url: `${API_URL}api/payroll/period/${period}`,
      headers: getHeaders(),
    });
    return response;
  } catch (error) {
    throw error;
  }
}

export async function getPayrollGraph() {
  try {
    const response = await axios({
      method: "get",
      url: `${API_URL}api/payroll/graph`,
      headers: getHeaders(),
    });
    return response.data;
  } catch (error) {
    throw error;
  }
}

export async function getGiftcardData() {
  try {
    const response = await axios({
      method: "get",
      url: `${API_URL}api/giftcard/stats`,
      headers: getHeaders(),
    });
    return response.data;
  } catch (error) {
    throw error;
  }
}

export async function getAllGiftcards() {
  try {
    const response = await axios({
      method: "get",
      url: `${API_URL}api/giftcard/all`,
      headers: getHeaders(),
    });
    return response.data;
  } catch (error) {
    throw error;
  }
}

export async function getGiftcardById(id) {
  try {
    const response = await axios({
      method: "get",
      url: `${API_URL}api/giftcard/${id}`,
      headers: getHeaders(),
    });
    return response.data;
  } catch (error) {
    throw error;
  }
}

export async function getReservationsData() {
  try {
    const response = await axios({
      method: "get",
      url: `${API_URL}api/reservations/stats`,
      headers: getHeaders(),
    });
    return response.data;
  } catch (error) {
    throw error;
  }
}

export async function getTimeblocks() {
  try {
    const response = await axios({
      method: "get",
      url: `${API_URL}api/reservations/timeblock`,
    });
    return response.data;
  } catch (error) {
    throw error;
  }
}

// ─── Waitlist ──────────────────────────────────────────────────────────────

export async function getWaitlist(signal) {
  try {
    const response = await axios({
      method: "get",
      url: `${API_URL}api/waitlist`,
      headers: getHeaders(),
      signal,
    });
    return response.data;
  } catch (error) {
    if (axios.isCancel(error)) {
      console.log("Request canceled", error.message);
    } else {
      throw error;
    }
  }
}

export async function getCancelledWaitlist(signal) {
  try {
    const response = await axios({
      method: "get",
      url: `${API_URL}api/waitlist/cancelled`,
      headers: getHeaders(),
      signal,
    });
    return response.data;
  } catch (error) {
    if (axios.isCancel(error)) {
      console.log("Request canceled", error.message);
    } else {
      throw error;
    }
  }
}

export async function postWaitlist(entry) {
  const response = await axios({
    method: "post",
    url: `${API_URL}api/waitlist`,
    headers: getHeaders(),
    data: entry,
  });
  return response.data;
}

export async function sendWaitlistReadyText(id) {
  const response = await axios({
    method: "post",
    url: `${API_URL}api/waitlist/${id}/text`,
    headers: getHeaders(),
  });
  return response.data;
}

export async function seatWaitlistAsWalkIn(id, user) {
  const response = await axios({
    method: "post",
    url: `${API_URL}api/waitlist/${id}/seat`,
    headers: getHeaders(),
    data: { user },
  });
  return response.data;
}

export async function cancelWaitlistItem(id, user) {
  const response = await axios({
    method: "patch",
    url: `${API_URL}api/waitlist/${id}/cancel`,
    headers: getHeaders(),
    data: { user },
  });
  return response.data;
}

export async function revertWaitlistItem(id, user) {
  const response = await axios({
    method: "patch",
    url: `${API_URL}api/waitlist/${id}/revert`,
    headers: getHeaders(),
    data: { user },
  });
  return response.data;
}

export async function deleteWaitlistItem(id) {
  const response = await axios({
    method: "delete",
    url: `${API_URL}api/waitlist/${id}`,
    headers: getHeaders(),
  });
  return response.data;
}

export async function deleteReservation(id) {
  const response = await axios({
    method: "delete",
    url: `${API_URL}api/reservations/id/${id}`,
    headers: getHeaders(),
  });
  return response.data;
}

// ─── Site Traffic ──────────────────────────────────────────────────────────

export async function trackPageview(route) {
  try {
    await axios({
      method: "post",
      url: `${API_URL}api/traffic/pageview`,
      data: { route, visitorId: getVisitorId(), device: getDeviceType() },
    });
  } catch (_err) {
    // tracking is best-effort, never bother the user
  }
}

export async function getTrafficStats() {
  const response = await axios({
    method: "get",
    url: `${API_URL}api/traffic/stats`,
    headers: getHeaders(),
  });
  return response.data;
}

// ─── Menu CMS ──────────────────────────────────────────────────────────────

export async function getCmsMenus() {
  const response = await axios({
    method: "get",
    url: `${API_URL}api/order/cms/menus`,
    headers: getHeaders(),
  });
  return response.data;
}

export async function cmsCreateSection(data) {
  const response = await axios({
    method: "post",
    url: `${API_URL}api/order/cms/sections`,
    headers: getHeaders(),
    data,
  });
  return response.data;
}

export async function cmsUpdateSection(id, data) {
  const response = await axios({
    method: "put",
    url: `${API_URL}api/order/cms/sections/${id}`,
    headers: getHeaders(),
    data,
  });
  return response.data;
}

export async function cmsReorderItems(sectionId, itemIds) {
  const response = await axios({
    method: "put",
    url: `${API_URL}api/order/cms/sections/${sectionId}/reorder`,
    headers: getHeaders(),
    data: { itemIds },
  });
  return response.data;
}

export async function cmsDeleteSection(id) {
  const response = await axios({
    method: "delete",
    url: `${API_URL}api/order/cms/sections/${id}`,
    headers: getHeaders(),
  });
  return response.data;
}

export async function cmsCreateItem(data) {
  const response = await axios({
    method: "post",
    url: `${API_URL}api/order/cms/items`,
    headers: getHeaders(),
    data,
  });
  return response.data;
}

export async function cmsUpdateItem(id, data) {
  const response = await axios({
    method: "put",
    url: `${API_URL}api/order/cms/items/${id}`,
    headers: getHeaders(),
    data,
  });
  return response.data;
}

export async function cmsUpdateStudioText(data) {
  const response = await axios({
    method: "patch",
    url: `${API_URL}api/order/cms/studio-text`,
    headers: getHeaders(),
    data,
  });
  return response.data;
}

export async function cmsDeleteItem(id) {
  const response = await axios({
    method: "delete",
    url: `${API_URL}api/order/cms/items/${id}`,
    headers: getHeaders(),
  });
  return response.data;
}

// ─── Menu Designer (print layout + decoration assets) ──────────────────────

export async function getCmsLayout() {
  const response = await axios({
    method: "get",
    url: `${API_URL}api/order/cms/layout`,
    headers: getHeaders(),
  });
  return response.data;
}

export async function saveCmsLayout(doc) {
  const response = await axios({
    method: "put",
    url: `${API_URL}api/order/cms/layout`,
    headers: getHeaders(),
    data: { doc },
  });
  return response.data;
}

export async function resetCmsLayout() {
  const response = await axios({
    method: "delete",
    url: `${API_URL}api/order/cms/layout`,
    headers: getHeaders(),
  });
  return response.data;
}

export async function getCmsAssets() {
  const response = await axios({
    method: "get",
    url: `${API_URL}api/order/cms/assets`,
    headers: getHeaders(),
  });
  return response.data;
}

export async function uploadCmsAsset(data) {
  const response = await axios({
    method: "post",
    url: `${API_URL}api/order/cms/assets`,
    headers: getHeaders(),
    data,
  });
  return response.data;
}

export async function deleteCmsAsset(id) {
  const response = await axios({
    method: "delete",
    url: `${API_URL}api/order/cms/assets/${id}`,
    headers: getHeaders(),
  });
  return response.data;
}

// Menu Studio: independent versioned design documents.
export async function loadMenuStudio(menuKey, options = {}) {
  const response = await axios.get(`${API_URL}api/menu-studio/${menuKey}${options.format === 2 ? "?format=2" : ""}`, { headers: getHeaders() });
  return response.data;
}
// Incremental save: { baseRevision, opId, ops, cms? } → { revision, cms }.
export async function saveMenuStudioOps(menuKey, batch) {
  const response = await axios.post(`${API_URL}api/menu-studio/${menuKey}/ops`, batch, { headers: getHeaders() });
  return response.data;
}
// Content-addressed artwork/font bytes, returned as a data URL. Immutable, so
// the browser cache serves repeat loads.
export async function loadMenuStudioAsset(hash) {
  const response = await fetch(`${API_URL}api/menu-studio/assets/${hash}`);
  if (!response.ok) throw new Error(`Artwork ${hash.slice(0, 8)} is unavailable.`);
  const blob = await response.blob();
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => resolve(reader.result);
    reader.onerror = reject;
    reader.readAsDataURL(blob);
  });
}
export async function menuStudioRecoveryPackage(menuKey, content) {
  const response = await axios.post(`${API_URL}api/menu-studio/${menuKey}/recovery`, { content }, { headers: getHeaders() });
  return response.data;
}
export async function menuStudioHistory(menuKey) {
  const response = await axios.get(`${API_URL}api/menu-studio/${menuKey}/history?limit=30`, { headers: getHeaders() });
  return response.data;
}
export async function menuStudioPresetReport() {
  const response = await axios.get(`${API_URL}api/menu-studio/presets/report`, { headers: getHeaders() });
  return response.data;
}
export async function saveMenuStudio(menuKey, doc, revision) {
  // clientFormat 2: this editor preserves storage format 2 and its structure.
  const response = await axios.put(`${API_URL}api/menu-studio/${menuKey}`, { doc, revision, clientFormat: 2 }, { headers: getHeaders() });
  return response.data;
}
